import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import visa from '../../assets/payment-logos/visa.svg';
import masterCard from '../../assets/payment-logos/mastercard.svg';
import amex from '../../assets/payment-logos/amex.svg';
import discover from '../../assets/payment-logos/discover.svg';
import jcb from '../../assets/payment-logos/jcb.svg';
// import upiLogo from '../../assets/payment-logos/upi.svg';
import dLocal from '../../assets/payment-logos/dlocal.svg'
import pagbrasil from '../../assets/payment-logos/pagbrasil.svg'
import PaysafePaymentForm from "../integration/paysafe/PaysafePaymentForm";
import DlocalUpiForm from "../integration/dlocal/DlocalUpiForm";
import { useCheckout } from "../../context/CheckoutContext";
import DlocalPixForm from "../integration/dlocal/DlocalPixForm";
import PagbrasilPaymentForm from "../integration/pagbrasil/PagbrasilPaymentForm";
import { useTranslation } from "../../hooks/useTranslation";
import AltruonDlocalUpiForm from "../integration/dlocal/AltruonDlocalUpiForm";
import AltruonDlocalPixForm from "../integration/dlocal/AltruonDlocalPixForm";
import AltruonDlocalCardForm from "../integration/dlocal/AltruonDlocalCardForm";
import AltruonPaysafePaymentForm from "../integration/paysafe/AltruonPaysafePaymentForm";
import AltruonCheckoutComCardForm from "../integration/checkout/AltruonCheckoutComCardForm";
import AltruonAdyenCardForm from "../integration/adyen/AltruonAdyenCardForm";
import AltruonCheckoutComKakaopayForm from "../integration/checkout/AltruonCheckoutComKakaopayForm";
import AltruonTrustlyOpenBankingForm from "../integration/trustly/AltruonTrustlyOpenBankingForm";
import { useStyles } from "../../context/StyleContext";
import { fetchGatewayConnection } from "../../api/api";
import PaymentMethodsSkeleton from "./PaymentMethodsSkeleton";
import AltruonBasisTheoryCardForm from "../integration/basistheory/AltruonBasisTheoryCardForm";
import {
  ALTRUON_VAULT_CARD_FORM_KEY,
  dedupeRoutingOptions,
  filterRoutingEntriesByCurrency,
  resolvePaymentForm,
} from "../../utils/routingEntries";
import { shouldLockPaymentMethodSelector } from "../../utils/sessionCheckout";
import AltruonPagbrasilPaymentForm from "../integration/pagbrasil/AltruonPagbrasilPaymentForm";
import AltruonPagbrasilCardForm from "../integration/pagbrasil/AltruonPagbrasilCardForm";
import AltruonNuveiCardForm from "../integration/nuvei/AltruonNuveiCardForm";
import AltruonCheckoutComApplePayForm from "../integration/checkout/AltruonCheckoutComApplePayForm";

// Static Styled Components with dynamic props
const SectionTitle = styled.h2`
  font-size: ${props => props.$styles?.typography?.fontSizes?.['2xl'] || '18px'};
  margin-bottom: 15px;
  font-weight: ${props => props.$styles?.typography?.fontWeights?.semibold || '600'};
  font-family: ${props => props.$styles?.typography?.fontFamilyHeadings || 'Arial, sans-serif'};
`;

const PaymentMethodsContainer = styled.div`
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: ${props => props.$styles?.spacing?.gap?.paymentOptions || '18px'};
`;

const PaymentOption = styled.div`
  border: none;
  border-radius: ${props => 
    props.$styles?.borderRadius?.xlarge || 
    props.$styles?.components?.paymentOption?.borderRadius || 
    '14px'};
  box-shadow: ${props => props.$styles?.components?.paymentOption?.shadow || '0 2px 12px rgba(60, 90, 130, 0.08)'};
  background: ${props => props.$styles?.components?.paymentOption?.backgroundColor || '#fff'};
  transition: ${props => props.$styles?.transitions?.default || '0.2s ease'};
  overflow: hidden;
  &:hover {
    box-shadow: ${props => props.$styles?.components?.paymentOption?.shadowHover || '0 4px 24px rgba(60, 90, 130, 0.14)'};
  }
`;

const PaymentOptionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.$styles?.spacing?.padding?.paymentOption || '16px 20px'};
  cursor: pointer;
  background: ${props => props.$selected
    ? props.$styles?.components?.paymentOption?.selectedBackgroundColor ||
      props.$styles?.colors?.primary ||
      '#eaf3ff'
    : props.$styles?.components?.paymentOption?.backgroundColor || '#fff'};
  border-left: ${props => props.$styles?.components?.paymentOption?.selectedBorderWidth || '5px'} solid ${props => 
    props.$selected 
      ? props.$styles?.colors?.borderSelected || 
        props.$styles?.colors?.accentPrimary || 
        props.$styles?.components?.paymentOption?.selectedBorderColor || 
        '#2563eb'
      : 'transparent'};
  transition: ${props => props.$styles?.transitions?.default || '0.2s ease'};
`;

const RadioButton = styled.div`
  width: ${props => props.$styles?.components?.radioButton?.size || '22px'};
  height: ${props => props.$styles?.components?.radioButton?.size || '22px'};
  border-radius: ${props => props.$styles?.borderRadius?.round || '50%'};
  border: ${props => props.$styles?.components?.radioButton?.borderWidth || '2px'} solid ${props => 
    props.$selected 
      ? props.$styles?.colors?.borderSelected || 
        props.$styles?.colors?.accentPrimary || 
        props.$styles?.components?.radioButton?.borderColorSelected || 
        '#2563eb'
      : props.$styles?.components?.radioButton?.borderColor || '#bfc9d9'};
  margin-right: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$styles?.components?.radioButton?.backgroundColor || '#fff'};

  &::after {
    content: "";
    width: ${props => props.$styles?.components?.radioButton?.innerSize || '12px'};
    height: ${props => props.$styles?.components?.radioButton?.innerSize || '12px'};
    border-radius: ${props => props.$styles?.borderRadius?.round || '50%'};
    background-color: ${props => props.$selected 
      ? props.$styles?.colors?.borderSelected || 
        props.$styles?.colors?.accentPrimary || 
        props.$styles?.components?.radioButton?.innerColorSelected || 
        '#2563eb'
      : 'transparent'};
    transition: ${props => props.$styles?.transitions?.default || '0.2s ease'};
  }
`;

const PaymentLabel = styled.div`
  flex: 1;
  font-weight: ${props => props.$selected 
    ? props.$styles?.typography?.fontWeights?.bold || '700'
    : props.$styles?.typography?.fontWeights?.medium || '500'};
  font-size: ${props => props.$styles?.typography?.fontSizes?.xl || '17px'};
  font-family: ${props => props.$styles?.typography?.fontFamily || 'Arial, sans-serif'};
  display: flex;
  align-items: center;
  color: ${props => props.$styles?.colors?.textPrimary || '#111827'};  /* headerTextColor → textPrimary */
`;

const GatewayBadge = styled.span`
  background: ${props => 
    props.$styles?.colors?.primary || 
    props.$styles?.components?.badge?.backgroundColor || 
    '#2563eb'};
  color: ${props => props.$styles?.components?.badge?.textColor || '#fff'};
  font-size: ${props => props.$styles?.components?.badge?.fontSize || '13px'};
  font-weight: ${props => props.$styles?.components?.badge?.fontWeight || '600'};
  border-radius: ${props => 
    props.$styles?.borderRadius?.pill || 
    props.$styles?.components?.badge?.borderRadius || 
    '999px'};
  padding: ${props => props.$styles?.components?.badge?.padding || '3px 14px'};
  margin-left: 14px;
  letter-spacing: ${props => props.$styles?.components?.badge?.letterSpacing || '0.5px'};
  display: inline-block;
  vertical-align: middle;
  box-shadow: ${props => props.$styles?.components?.badge?.shadow || '0 1px 4px rgba(37,99,235,0.08)'};
`;

const PaymentIcons = styled.div`
  display: flex;
  gap: ${props => props.$styles?.spacing?.gap?.icons || '10px'};
`;

const PaymentIcon = styled.img`
  height: ${props => props.$styles?.components?.paymentIcon?.height || '28px'};
  width: ${props => props.$styles?.components?.paymentIcon?.width || 'auto'};
`;

const PaymentDrawer = styled.div`
  padding: ${props => props.$styles?.spacing?.padding?.drawer || '16px 20px'};
  border-top: none;
  background-color: ${props =>
    props.$transparent
      ? 'transparent'
      : (props.$styles?.colors?.pageBackground ||
         props.$styles?.colors?.containerBackground ||
         'rgb(247, 250, 253)')};
`;

const DrawerContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  width: 100%;
`;

const RedirectMessage = styled.div`
  text-align: center;
  color: #666;
  font-size: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RedirectIcon = styled.div`
  width: 60px;
  height: 60px;
  border: 1px solid #ddd;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  position: relative;

  &::after {
    content: "→";
    font-size: 24px;
    position: absolute;
  }
`;

// Map payment methods and platforms to their forms
const paymentMethodForms = {
  "card_paysafe": AltruonPaysafePaymentForm,
  "card_dlocal": AltruonDlocalCardForm,
  "card_checkoutcom": AltruonCheckoutComCardForm,
  "card_adyen": AltruonAdyenCardForm,
  "card_pagbrasil": AltruonPagbrasilCardForm,
  "upi_dlocal": AltruonDlocalUpiForm,
  "pix_dlocal": AltruonDlocalPixForm,
  "pix_pagbrasil": AltruonPagbrasilPaymentForm,
  "kakaopay_checkoutcom": AltruonCheckoutComKakaopayForm,
  "openbanking_trustly": AltruonTrustlyOpenBankingForm,
  "card_nuvei": AltruonNuveiCardForm,
  "applepay_checkoutcom": AltruonCheckoutComApplePayForm,
  [ALTRUON_VAULT_CARD_FORM_KEY]: AltruonBasisTheoryCardForm,
  // Add more mappings as needed
};


// Map payment methods to their labels - these will be replaced by translation function
const getPaymentMethodLabels = (t) => ({
  card: t('checkout.paymentMethods.labels.card'),
  upi: t('checkout.paymentMethods.labels.upi'),
  pix: t('checkout.paymentMethods.labels.pix'),
  kakaopay: t('checkout.paymentMethods.labels.kakaopay'),
  openbanking: t('checkout.paymentMethods.labels.openbanking'),
  // Add more as needed
});

// Map payment methods and platforms to their icons
const paymentMethodIcons = {
  card: [visa, masterCard, amex, discover, jcb],
  upi: {
    dlocal: [], // Add UPI icon if needed
  },
  // pix: {   // enable PIX icon if needed
  //   dlocal: [dLocal], 
  //   pagbrasil: [pagbrasil],
  // },
};

const AltruonPaymentMethodSelector = ({
  selectedMethod,
  onChange,
  routingEntries,
  formData,
  itemDetails,
  billingPlatformId,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,
  paymentMethodPinnedAtCreate = false,
  isLoading,
  isRoutingLoaded = true,
  transparent,
  tenantId
}) => {
  
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState(null);
  const [dlocalCardGatewayConnection, setDlocalCardGatewayConnection] = useState(null);
  const { setPaymentMethodName, setPlatformName } = useCheckout();
  const { styles } = useStyles();

  // Specific-mode sessions (merchant pinned paymentMethod at create) render the form directly.
  const lockPaymentMethodSelector = shouldLockPaymentMethodSelector(
    checkoutProcessDto,
    paymentMethodPinnedAtCreate
  );

  // Filter routing entries by current currency
  const currentCurrency = itemDetails?.currencyCode;
  const uniqueOptions = useMemo(
    () =>
      dedupeRoutingOptions(
        filterRoutingEntriesByCurrency(routingEntries, currentCurrency).map(
          (entry) => ({
            method: entry.paymentMethod,
            platform: entry.platform,
            currency: entry.currency,
            id: entry.id,
            vaulting: entry.vaulting,
          })
        )
      ),
    [routingEntries, currentCurrency]
  );

  const dlocalCardRoutingId = useMemo(
    () =>
      uniqueOptions.find(
        (opt) => opt.method === "card" && opt.platform === "dlocal"
      )?.id ?? null,
    [uniqueOptions]
  );


  const isPaymentMethodsLoading = isLoading || !isRoutingLoaded;

  // Prefetch dLocal card gateway connection once per routing id.
  // Altruon mounts the card form immediately, so the form also calls
  // fetchGatewayConnection — the shared cache ensures one network request.
  useEffect(() => {
    if (!dlocalCardRoutingId) {
      setDlocalCardGatewayConnection(null);
      return;
    }

    let cancelled = false;

    fetchGatewayConnection({ id: dlocalCardRoutingId })
      .then((data) => {
        if (!cancelled) setDlocalCardGatewayConnection(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(
          "❌ Failed to prefetch dLocal card gateway connection:",
          error
        );
      });

    return () => {
      cancelled = true;
    };
  }, [dlocalCardRoutingId]);

  const handleMethodSelect = (method, platform) => {
    const formKey = `${method}_${platform}`.toLowerCase();
    onChange(method);
    setOpenDrawer(formKey);
    setPaymentMethodName(method);
    setPlatformName(platform);
  };

  // Match PaymentMethodSelector: mount only the selected form. Auto-open the
  // first method so embedded checkout still shows a form without mounting every
  // gateway (each mount was triggering its own gatewayConnection).
  useEffect(() => {
    if (openDrawer || !uniqueOptions.length) return;
    const { method, platform } = uniqueOptions[0];
    handleMethodSelect(method, platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueOptions, openDrawer]);

  // If session data contains payment details, render form directly without drawer UI
  React.useEffect(() => {
    if (lockPaymentMethodSelector) {
      const method = checkoutProcessDto.paymentData.paymentMethod;
      const platform = checkoutProcessDto.paymentData.gatewayName;
      setPaymentMethodName(method);
      setPlatformName(platform);
    }
  }, [lockPaymentMethodSelector, checkoutProcessDto, setPaymentMethodName, setPlatformName]);

  if (lockPaymentMethodSelector) {
    const method = checkoutProcessDto.paymentData.paymentMethod;
    const platform = checkoutProcessDto.paymentData.gatewayName;
    const formKey = `${method}_${platform}`.toLowerCase();
    const pinnedEntry = (routingEntries || []).find(
      (entry) => entry.paymentMethod === method && entry.platform === platform
    );
    const FormComponent = resolvePaymentForm(
      { method, platform, vaulting: pinnedEntry?.vaulting },
      paymentMethodForms
    );
    
    if (FormComponent) {
      return (
        <FormComponent
          formData={formData}
          itemDetails={itemDetails}
          billingPlatformId={billingPlatformId}
          routingEntries={routingEntries}
          planId={planId}
          platformItemDetails={platformItemDetails}
          checkoutPageConfDto={checkoutPageConfDto}
          checkoutProcessDto={checkoutProcessDto}
          transparent={transparent}
          prefetchedGatewayConnection={
            formKey === 'card_dlocal' ? dlocalCardGatewayConnection : undefined
          }
          tenantId={tenantId}
        />
      );
    }
  }

  return (
    <>
      {isPaymentMethodsLoading ? (
        <PaymentMethodsSkeleton showTitle={false} />
      ) : (
        uniqueOptions.length === 0 ? (
          <RedirectMessage>
            <RedirectIcon />
            <div style={{ color: "red", fontSize: "16px" }}>
              {t('checkout.paymentMethods.noMethods')}
            </div>
          </RedirectMessage>
        ) : (
          <PaymentMethodsContainer $styles={styles}>
            {/* <SectionTitle $styles={styles}>{t('checkout.paymentMethods.title')}</SectionTitle> */}
            {uniqueOptions.map((option, idx) => {
              const { method, platform } = option;
              const formKey = `${method}_${platform}`.toLowerCase();
              const FormComponent = resolvePaymentForm(option, paymentMethodForms);
              if (!FormComponent) {
                console.warn(`⚠️ Component not found for: ${formKey}`);
                return null;
              }
              const isSelected = openDrawer === formKey;
              return (
                <PaymentOption key={formKey} $styles={styles}>
                  <PaymentOptionHeader
                    $styles={styles}
                    $selected={isSelected}
                    onClick={() => handleMethodSelect(method, platform)}
                  >
                    <RadioButton $styles={styles} $selected={isSelected} />
                    <PaymentLabel $styles={styles} $selected={isSelected}>
                      {getPaymentMethodLabels(t)[method] || method.toUpperCase()}
                      {/* <GatewayBadge $styles={styles}>{platform}</GatewayBadge> */}
                    </PaymentLabel>
                    <PaymentIcons $styles={styles}>
                      {(paymentMethodIcons[method] && paymentMethodIcons[method][platform]
                        ? paymentMethodIcons[method][platform]
                        : paymentMethodIcons[method] || []
                      ).map((icon, i) => (
                        <PaymentIcon $styles={styles} key={i} src={icon} alt={method} />
                      ))}
                    </PaymentIcons>
                  </PaymentOptionHeader>
                  {isSelected && (
                    <PaymentDrawer $styles={styles} $transparent={transparent}>
                      <DrawerContent>
                        <FormComponent
                          formData={formData}
                          itemDetails={itemDetails}
                          billingPlatformId={billingPlatformId}
                          routingEntries={routingEntries}
                          planId={planId}
                          platformItemDetails={platformItemDetails}
                          checkoutPageConfDto={checkoutPageConfDto}
                          checkoutProcessDto={checkoutProcessDto}
                          transparent={transparent}
                          prefetchedGatewayConnection={
                            formKey === 'card_dlocal' ? dlocalCardGatewayConnection : undefined
                          }
                          tenantId={tenantId}
                        />
                      </DrawerContent>
                    </PaymentDrawer>
                  )}
                </PaymentOption>
              );
            })}
          </PaymentMethodsContainer>
        )
      )}
    </>
  );
};

export default AltruonPaymentMethodSelector;